import { useEffect, useRef, useState } from 'react'
import type { CSSProperties, MouseEvent as ReactMouseEvent, PointerEvent as ReactPointerEvent, SyntheticEvent } from 'react'
import { PDF_PREVIEW_DEFAULT_ZOOM } from './previewZoom'
import { NO_PAN, clampPan, clampZoom, fitImageSize, panForZoomAtPoint, rotatedBox, zoomAfterWheel } from './documentViewTransform'
import type { Point, Size } from './documentViewTransform'

const DOUBLE_CLICK_ZOOM = 2

interface UseImagePanZoomOptions {
    zoom: number
    rotation: number
    onZoomChange: (zoom: number) => void
}

const pointFromFrameCentre = (frame: HTMLElement, clientX: number, clientY: number): Point => {
    const bounds = frame.getBoundingClientRect()
    return { x: clientX - bounds.left - bounds.width / 2, y: clientY - bounds.top - bounds.height / 2 }
}

/**
 * Wheel-to-zoom, drag-to-pan and double-click zoom for an image centred in a fixed frame. Zoom and rotation are
 * owned by the caller (the toolbar drives them too); the pan offset lives here and is re-clamped on every render,
 * so a zoom or rotation change from outside can never leave the image stranded off-screen.
 */
export const useImagePanZoom = ({ zoom, rotation, onZoomChange }: UseImagePanZoomOptions) => {
    const frameRef = useRef<HTMLDivElement>(null)
    const dragRef = useRef<{ pointerId: number; start: Point; pan: Point } | null>(null)
    const [frame, setFrame] = useState<Size>({ width: 0, height: 0 })
    const [natural, setNatural] = useState<Size | null>(null)
    const [rawPan, setRawPan] = useState<Point>(NO_PAN)
    const [dragging, setDragging] = useState(false)

    const fitted = natural ? fitImageSize(natural, frame, rotation) : null
    const drawn = fitted && fitted.width > 0 ? { width: fitted.width * zoom, height: fitted.height * zoom } : null
    const content = drawn ? rotatedBox(drawn, rotation) : null
    const pan = content ? clampPan(rawPan, content, frame) : NO_PAN
    const pannable = Boolean(content && (content.width > frame.width || content.height > frame.height))

    // Wheel events can arrive faster than React re-renders, so each one builds on the view the previous one
    // produced rather than on the zoom and pan of the last render; otherwise all but one of a burst is lost.
    const view = useRef({ zoom, pan })
    useEffect(() => {
        view.current = { zoom, pan }
    })

    const zoomTo = (requestedZoom: (zoom: number) => number, point: Point) => {
        const current = view.current
        const nextZoom = clampZoom(requestedZoom(current.zoom))
        if (nextZoom === current.zoom || !fitted) return
        const nextContent = rotatedBox({ width: fitted.width * nextZoom, height: fitted.height * nextZoom }, rotation)
        const nextPan = clampPan(panForZoomAtPoint(current.pan, current.zoom, nextZoom, point), nextContent, frame)
        view.current = { zoom: nextZoom, pan: nextPan }
        setRawPan(nextPan)
        onZoomChange(nextZoom)
    }

    const onWheel = (event: WheelEvent) => {
        event.preventDefault()
        if (!frameRef.current) return
        zoomTo((current) => zoomAfterWheel(current, event), pointFromFrameCentre(frameRef.current, event.clientX, event.clientY))
    }
    // The native listener below is attached once; this keeps it calling the handler of the latest render.
    const onWheelRef = useRef(onWheel)
    useEffect(() => {
        onWheelRef.current = onWheel
    })

    useEffect(() => {
        const element = frameRef.current
        if (!element) return

        const updateSize = () => setFrame({ width: element.clientWidth, height: element.clientHeight })
        updateSize()

        // React registers wheel listeners as passive, so preventDefault (which stops the page behind the
        // viewer from scrolling) needs a native non-passive listener.
        const handleWheel = (event: WheelEvent) => onWheelRef.current(event)
        element.addEventListener('wheel', handleWheel, { passive: false })

        const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateSize)
        resizeObserver?.observe(element)
        if (!resizeObserver) window.addEventListener('resize', updateSize)

        return () => {
            element.removeEventListener('wheel', handleWheel)
            resizeObserver?.disconnect()
            if (!resizeObserver) window.removeEventListener('resize', updateSize)
        }
    }, [])

    const stopDragging = (pointerId: number) => {
        if (dragRef.current?.pointerId !== pointerId) return
        const element = frameRef.current
        if (element?.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId)
        dragRef.current = null
        setDragging(false)
    }

    const frameHandlers = {
        onPointerDown: (event: ReactPointerEvent<HTMLDivElement>) => {
            if (!pannable || (event.pointerType === 'mouse' && event.button !== 0)) return
            dragRef.current = { pointerId: event.pointerId, start: { x: event.clientX, y: event.clientY }, pan }
            event.currentTarget.setPointerCapture(event.pointerId)
            setDragging(true)
            event.preventDefault()
        },
        onPointerMove: (event: ReactPointerEvent<HTMLDivElement>) => {
            const drag = dragRef.current
            if (!drag || drag.pointerId !== event.pointerId) return
            setRawPan({ x: drag.pan.x + event.clientX - drag.start.x, y: drag.pan.y + event.clientY - drag.start.y })
        },
        onPointerUp: (event: ReactPointerEvent<HTMLDivElement>) => stopDragging(event.pointerId),
        onPointerCancel: (event: ReactPointerEvent<HTMLDivElement>) => stopDragging(event.pointerId),
        onDoubleClick: (event: ReactMouseEvent<HTMLDivElement>) => {
            const point = pointFromFrameCentre(event.currentTarget, event.clientX, event.clientY)
            zoomTo((current) => (current > PDF_PREVIEW_DEFAULT_ZOOM ? PDF_PREVIEW_DEFAULT_ZOOM : DOUBLE_CLICK_ZOOM), point)
        },
    }

    // Until the image has reported its pixels there is nothing to size, so it is only centred and rotated.
    const imageStyle: CSSProperties = {
        ...(drawn ? { width: drawn.width, height: drawn.height } : {}),
        transform: `translate(-50%, -50%) translate(${pan.x}px, ${pan.y}px) rotate(${rotation}deg)`,
    }

    const onImageLoad = (event: SyntheticEvent<HTMLImageElement>) =>
        setNatural({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight })

    const cursor = pannable ? (dragging ? 'cursor-grabbing' : 'cursor-grab') : zoom > PDF_PREVIEW_DEFAULT_ZOOM ? 'cursor-zoom-out' : 'cursor-zoom-in'

    return { frameRef, frameHandlers, imageStyle, onImageLoad, cursor }
}
